import RADApplication from '@/Management/RADApplication';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import ResourceManager from '@/Management/ResourceManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import { IResultDTO } from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import ComponentManager from '@/Management/ComponentManager';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';

export default class MediumUploadHelper {

    /**
     * Die Größe eines Chunks
     */
    private static CHUNK_SIZE = 4 * 1024 * 1024;

    /**
     * Die maximale Größe einer Datei
     */
    private static MAX_FILE_SIZE = MODEL.FileUploadLimitInMB * 1024 * 1024;

    private static MAX_FILE_AMOUNT: number = MODEL.MaxBulkUploadFiles || 20;

    /**
     * Sendet Dateien zum Backend
     * @param contextPdoId
     * @param contextTypeName
     * @param relationType
     * @param relationName
     * @param files
     * @returns JQueryPromise
     */
    public static handleGridFiles(contextPdoId: string, contextTypeName: string, relationType: string, relationName: string, files: File[]): {[fileName: string]: JQueryPromise<IResultDTO>} {
        const result: {[fileName: string]: JQueryPromise<IResultDTO>} = {};

        if (this.isFileLimitExceeded(files.length)) {
            this.showTooManyFilesMessageBox();
            return result;
        }

        const errorCodeToFileNames: Record<number, string[]> = {};
        for (const file of files) {
            result[file.name] = this.handleGridFile(contextPdoId, contextTypeName, relationType, relationName, file, statusCode => {
                if (!errorCodeToFileNames[statusCode])
                    errorCodeToFileNames[statusCode] = [];

                errorCodeToFileNames[statusCode].push(file.name);
            });
        }

        Promise.all(Object.values(result)).finally(() => {
            for (const statusCode in errorCodeToFileNames) {
                const fileNames = errorCodeToFileNames[statusCode].join(", ");
                this.showFileUploadError(+statusCode, fileNames);
            }
        });

        return result;
    }

    private static isFileLimitExceeded(amount: number) {
        return amount > this.MAX_FILE_AMOUNT;
    }

    private static showTooManyFilesMessageBox() {
        const message = ResourceManager.getInstance().get('FILE_UPLOAD_TOO_MANY_FILES')
            .replace('$$fileAmount$$', this.MAX_FILE_AMOUNT.toString());
        const messageBox = MessageBoxViewModel.createNoticeMessageBox(message);
        this.showMessageBox(messageBox);
    }

    /**
     * Sendet eine Datei zum Backend
     */
    public static handleGridFile(contextPdoId: string, contextTypeName: string, relationType: string, relationName: string, file: File, onError?: (statusCode: number) => void): JQueryPromise<IResultDTO> {
        if (file.size >= MediumUploadHelper.MAX_FILE_SIZE) {
            let content: string = ResourceManager.getInstance().get('FILE_SIZE_EXCEED_PARAM');
            content = content.replace('$$size$$', "" + MODEL.FileUploadLimitInMB)
            this.showMessageBox(MessageBoxViewModel.createErrorMessageBoxWithoutErgnames(content), file.name);
            return Utilities.resolveImmediately(null);
        }

        // Verzeichniserkennung
        if (!file.type && (file.size % 4096 === 0)) {
            this.showMessageBox(MessageBoxViewModel.createErrorMessageBox(null, 'FILE_IS_INVALID'), file.name);
            return Utilities.resolveImmediately(null);
        }

        const deferred = $.Deferred();
        const extension = MediumUploadHelper.getExtension(file.name);
        MediumUploadHelper.sendFile(file, extension, 0, true,
            // onSuccess
            () => {
                MediumUploadHelper.handleGridContent(contextPdoId, contextTypeName, relationType, relationName, true, file.name)
                    .always((result) => {
                        deferred.resolve(result);
                    });
            },
            // onError
            (statusCode) => {
                if (onError)
                    onError(statusCode);

                deferred.resolve(null);
            });

        return deferred.promise();
    }

    private static showMessageBox(vm: MessageBoxViewModel, prefix?: string) {
        if (prefix) {
            const LabelVM = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
            vm.label.prefix = new LabelVM({ caption: `${prefix}: ` });
        }
        RADApplication.getInstance().showMessageBox(vm);
    }

    /**
     * Wenn der Datei Upload fehlgeschlagen ist
     */
    public static showFileUploadError(httpStatus?: number, prefix?: string): void {
        const MessageBoxVM = ComponentManager.getViewModel<typeof MessageBoxViewModel>('MessageBoxViewModel');

        let messageVm: MessageBoxViewModel;
        if (httpStatus === 400) {
            messageVm = MessageBoxVM.createErrorMessageBox(null, 'FILE_UPLOAD_ERROR_400');
        }
        else if (httpStatus === 406) {
            messageVm = MessageBoxVM.createErrorMessageBox(null, 'FILE_UPLOAD_ERROR_406');
        }
        else if (httpStatus === 413) {
            const content: string = ResourceManager.getInstance().get('FILE_SIZE_EXCEED_PARAM')
                .replace('$$size$$', "" + MODEL.FileUploadLimitInMB);
            messageVm = MessageBoxVM.createErrorMessageBoxWithoutErgnames(content);
        }
        else if (httpStatus === 415) {
            messageVm = MessageBoxVM.createErrorMessageBox(null, 'FILE_UPLOAD_ERROR_415');
        }
        else {
            messageVm = MessageBoxVM.createErrorMessageBox(null, 'GENERIC_ERROR');
        }

        this.showMessageBox(messageVm, prefix);
    }

    /**
     * Sendet eine Request zum Erstellen eines Dokuments an das Backend.
     * @param contextPdoId
     * @param contextTypeName
     * @param relationType
     * @param relationName
     * @param isFile
     * @param name
     * @return JQueryPromise
     */
    public static handleGridContent(contextPdoId: string, contextTypeName: string, relationType: string, relationName: string, isFile: boolean, name: string): JQueryPromise<IResultDTO> {
        return RADEnvironmentProxy.getInstance().createAndConnectFromUploadedContent({
            contextPdoId: contextPdoId,
            contextPdTypeName: contextTypeName,
            targetPdTypeName: relationType,
            relationName: relationName,
            name: isFile ? this.escapeFileName(name) : name,
            isFile: isFile
        }).promise();
    }

    private static fileNameReplacements = {
        '#': '$_HASH_$',
        ';': '$_SEMICOLON_$',
        ',': '$_COMMA_$',
        '\'': '$_APOSTROPHE_$',
    } as const;

    /**
     * Erstellt einen neuen Dateinamen, welcher vom Backend nicht als mögliche Path-Traversal/Escaping-Attacke erkannt wird.
     * @param fileName Der ursprüngliche Dateiname
     * @private
     */
    private static escapeFileName(fileName: string): string {
        return Object.keys(this.fileNameReplacements)
            .reduce((name, key) => name.replace(new RegExp(key), this.fileNameReplacements[key]), fileName);
    }

    /**
     * Sendet die Datei in einzelne Chunks aufgeteilt an den MediumHandler vom Backend mit einem Ajax-request.
     */
    private static sendFile(
        file: File,
        extension: string,
        startByte: number,
        overwrite: boolean,
        onSuccess: Function,
        onError: (statusCode: number, textStatus: string, errorThrown: string) => void = () => { }
    ): void {
        const mediumHandler: string = RADApplication.getInstance().radEnvironmentMediumHandler;
        const complete: boolean = startByte + MediumUploadHelper.CHUNK_SIZE >= file.size;
        const fileName = encodeURIComponent(this.escapeFileName(file.name));

        MediumUploadHelper.createChunk(file, startByte, (data, bytesLoaded) => {
            $.ajax({
                url: `${mediumHandler}?Filename=${fileName}&Extension=${extension}&FullFileSize=${file.size}&StartByte=${startByte}&Complete=${complete}&Overwrite=${overwrite}`,
                type: 'POST',
                beforeSend: (request) => {
                    request.setRequestHeader('token', RADApplication.getInstance().token)
                },
                cache: false,
                async: true,
                processData: false,
                data: data,
                contentType: false,
                success: (data) => {
                    if (data === 'OKAY') {
                        if (bytesLoaded < file.size) {
                            MediumUploadHelper.sendFile(file, extension, bytesLoaded, overwrite, onSuccess, onError);
                        } else {
                            onSuccess();
                        }
                    } else {
                        this.showMessageBox(MessageBoxViewModel.createErrorMessageBox(null, 'GENERIC_ERROR'), file.name);
                    }
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    onError(jqXHR.status, textStatus, errorThrown);
                }
            });
        });
    }

    /**
     * Erstellt einen Chunk aus der ausgewählten Datei und übergibt dessen Daten an einen Callback
     * @param file
     * @param startByte
     * @param callback
     */
    private static createChunk(file: File, startByte: number, callback: Function): any {
        const stopByte: number = Math.min(file.size, startByte + MediumUploadHelper.CHUNK_SIZE);
        const reader: FileReader = new FileReader();
        const blob: Blob = file.slice(startByte, stopByte);

        reader.onloadend = (event: ProgressEvent) => {
            const eventFileReader: FileReader = event.target as FileReader;

            if (eventFileReader.readyState === FileReader.DONE) {
                return callback(eventFileReader.result, stopByte);
            }
        };

        reader.readAsArrayBuffer(blob);
    }

    /**
     * Gibt die Dateiendung zurück
     * @param name
     */
    private static getExtension(name: string): string {
        if (name) {
            const splits = name.split('.');
            if (splits.length <= 1)
                return '';

            return splits.pop();
        }

        return '';
    }
}